import { useCallback } from 'react';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import { sendMessage, addRecipients, createNewConversation } from 'src/redux/slices/chat';
// hooks
import { useAuthContext } from 'src/auth/hooks';
// routes
import { paths } from 'src/routes/paths';
// components
import { useRouter } from 'src/routes/hook';

// ----------------------------------------------------------------------

const baseUrl = paths.dashboard.chat.root;

export default function useChat() {
  const dispatch = useDispatch();

  const router = useRouter();

  const { user } = useAuthContext();

  const currentUserId = user?.id;

  const { contacts, recipients, conversations, currentConversationId, conversationsStatus } =
    useSelector((state) => state.chat);

  const currentConversation = useSelector(() => {
    if (currentConversationId) {
      // Try both string and number versions of the ID
      const conversation = conversations.byId[currentConversationId] ||
                          conversations.byId[String(currentConversationId)] ||
                          conversations.byId[Number(currentConversationId)];

      if (conversation) {
        return conversation;
      }
    }

    return {
      id: '',
      messages: [],
      participants: [],
      unreadCount: 0,
      type: '',
    };
  });

  const participantsInConversation = currentConversation.participants.filter(
    (participant) => String(participant.id) !== String(currentUserId)
  );

  const onClickNavItem = useCallback(
    (conversationId) => {
      let _conversationId;

      const conversation = conversations.byId[conversationId];

      if (!conversation) {
        // If no conversation found, the conversationId IS the user ID we want to navigate to
        _conversationId = typeof conversationId === 'object' ? conversationId.id : conversationId;
      } else if (conversation.type === 'GROUP') {
        _conversationId = conversation.id;
      } else {
        const otherParticipants = conversation.participants.filter(
          (participant) => participant.id !== currentUserId
        );

        if (otherParticipants.length > 0) {
          const participantId = otherParticipants[0].id;
          // Ensure participantId is a primitive, not an object
          _conversationId = typeof participantId === 'object' ? participantId.id || String(participantId) : participantId;
        } else {
          // Fallback to conversation ID
          _conversationId = conversationId;
        }
      }

      router.push(`${baseUrl}/${_conversationId}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversations.byId, currentUserId]
  );

  const onSendMessage = useCallback(
    async (body, messageType = 'text', mediaUrl = null, options = {}) => {
      try {
        if (currentConversationId) {
          // currentConversationId is the receiverId (otherUserId) in the new API
          return await dispatch(sendMessage(currentConversationId, body, messageType, mediaUrl, options));
        }
        return undefined;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [currentConversationId, dispatch]
  );

  const onSendCompose = useCallback(
    (body) => {
      try {
        dispatch(createNewConversation(recipients, body));
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch, recipients]
  );

  const onAddRecipients = useCallback(
    (selected) => {
      dispatch(addRecipients(selected));
    },
    [dispatch]
  );

  return {
    // redux
    contacts,
    recipients,
    conversations,
    conversationsStatus,
    currentConversationId,
    //
    currentConversation,
    participantsInConversation,
    //
    onSendMessage,
    onSendCompose,
    onAddRecipients,
    onClickNavItem,
  };
}
